const html5CheatSheet = {
  id: "html5",
  title: "HTML5 Cheatsheet",
  description:
    "Complete guide to HTML5 elements, tags, and best practices for modern web development",
  language: "html",
  category: "Frontend Development",
  level: "All Levels",
  sections: [
    {
      title: "HTML Basics",
      items: [
        {
          title: "Document Structure",
          content: "Basic HTML5 document structure",
          code: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Page Title</title>
</head>
<body>
    <!-- Content here -->
</body>
</html>`,
          note: "Always include DOCTYPE and viewport meta tag",
        },
        {
          title: "Comments",
          content: "HTML comments for documentation",
          code: `<!-- This is a comment -->
<!-- 
    Multi-line
    comment
-->`,
          note: "Comments are not displayed in the browser",
        },
      ],
    },
    {
      title: "Text Elements",
      items: [
        {
          title: "Headings",
          content: "Six levels of section headings",
          code: `<h1>Main Title</h1>
<h2>Subtitle</h2>
<h3>Section Title</h3>
<h4>Subsection Title</h4>
<h5>Minor Title</h5>
<h6>Smallest Title</h6>`,
          note: "Use only one h1 per page",
        },
        {
          title: "Text Formatting",
          content: "Basic text formatting elements",
          code: `<p>Paragraph text</p>
<strong>Bold text</strong>
<em>Italic text</em>
<mark>Highlighted text</mark>
<small>Smaller text</small>
<del>Deleted text</del>
<ins>Inserted text</ins>
<sub>Subscript</sub>
<sup>Superscript</sup>`,
          note: "Use semantic elements over style-based elements",
        },
      ],
    },
    {
      title: "Links & Lists",
      items: [
        {
          title: "Links",
          content: "Different types of hyperlinks",
          code: `<a href="https://example.com">External Link</a>
<a href="/about">Internal Link</a>
<a href="#section">Page Section</a>
<a href="mailto:email@example.com">Email Link</a>
<a href="tel:+1234567890">Phone Link</a>
<a href="file.pdf" download>Download Link</a>`,
          note: "Use descriptive text for better accessibility",
        },
        {
          title: "Lists",
          content: "Different types of lists",
          code: `<ul>
    <li>Unordered list item</li>
    <li>Another item</li>
</ul>

<ol>
    <li>First ordered item</li>
    <li>Second ordered item</li>
</ol>

<dl>
    <dt>Term</dt>
    <dd>Definition</dd>
</dl>`,
          note: "Lists can be nested inside other lists",
        },
      ],
    },
    {
      title: "Media",
      items: [
        {
          title: "Images",
          content: "Image elements with attributes",
          code: `<img src="image.jpg" alt="Description" width="300" height="200">
<picture>
    <source media="(min-width: 800px)" srcset="large.jpg">
    <source media="(min-width: 400px)" srcset="medium.jpg">
  <img src="small.jpg" alt="Responsive image">
</picture>
<figure>
    <img src="image.jpg" alt="Description">
    <figcaption>Image caption</figcaption>
</figure>`,
          note: "Always include alt text for accessibility",
        },
        {
          title: "Audio & Video",
          content: "Multimedia elements",
          code: `<audio controls>
    <source src="audio.mp3" type="audio/mpeg">
    <source src="audio.ogg" type="audio/ogg">
    Your browser does not support audio.
</audio>

<video width="640" height="360" controls>
  <source src="video.mp4" type="video/mp4">
  <source src="video.webm" type="video/webm">
    <track src="captions.vtt" kind="captions">
    Your browser does not support video.
</video>`,
          note: "Provide multiple formats for better compatibility",
        },
      ],
    },
    {
      title: "Forms",
      items: [
        {
          title: "Form Structure",
          content: "Basic form elements",
          code: `<form action="/submit" method="post">
    <label for="username">Username:</label>
    <input type="text" id="username" name="username" required>
    
    <label for="password">Password:</label>
    <input type="password" id="password" name="password" required>
    
    <button type="submit">Submit</button>
</form>`,
          note: "Always use labels with form controls",
        },
        {
          title: "Input Types",
          content: "HTML5 input types",
          code: `<input type="text" placeholder="Text input">
<input type="email" placeholder="Email">
<input type="password" placeholder="Password">
<input type="number" min="0" max="100">
<input type="tel" placeholder="Phone">
<input type="date">
<input type="time">
<input type="file" accept="image/*">
<input type="checkbox" checked>
<input type="radio" name="option">
<input type="color">
<input type="range" min="0" max="100">`,
          note: "Use appropriate input types for better UX",
        },
        {
          title: "Form Elements",
          content: "Additional form controls",
          code: `<select name="country">
    <option value="">Select country</option>
    <option value="us">United States</option>
    <option value="uk">United Kingdom</option>
</select>

<textarea name="message" rows="4" cols="50"></textarea>

<fieldset>
    <legend>Contact Preference</legend>
    <label>
        <input type="radio" name="contact" value="email">
        Email
    </label>
    <label>
        <input type="radio" name="contact" value="phone">
        Phone
    </label>
</fieldset>`,
          note: "Group related form elements with fieldset",
        },
      ],
    },
    {
      title: "Tables",
      items: [
        {
          title: "Basic Table",
          content: "Table structure with headers",
          code: `<table>
    <caption>Monthly Report</caption>
    <thead>
        <tr>
            <th>Header 1</th>
            <th>Header 2</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <td>Data 1</td>
            <td>Data 2</td>
        </tr>
    </tbody>
    <tfoot>
        <tr>
            <td>Footer 1</td>
            <td>Footer 2</td>
        </tr>
    </tfoot>
</table>`,
          note: "Use tables for tabular data only",
        },
        {
          title: "Table Features",
          content: "Advanced table attributes",
          code: `<table>
    <tr>
        <td colspan="2">Spans two columns</td>
    </tr>
    <tr>
        <td rowspan="2">Spans two rows</td>
        <td>Normal cell</td>
    </tr>
</table>`,
          note: "Use colspan and rowspan for complex layouts",
        },
      ],
    },
    {
      title: "Semantic Elements",
      items: [
        {
          title: "Layout Elements",
          content: "Semantic page structure",
          code: `<header>Site header</header>
<nav>Navigation menu</nav>
<main>
    <article>
        <section>Article section</section>
    </article>
    <aside>Sidebar content</aside>
</main>
<footer>Site footer</footer>`,
          note: "Use semantic elements for better accessibility",
        },
        {
          title: "Text Semantics",
          content: "Semantic text elements",
          code: `<article>Complete article content</article>
<section>Grouped content</section>
<aside>Related content</aside>
<details>
    <summary>Click to expand</summary>
    Detailed content here
</details>
<time datetime="2024-01-01">January 1, 2024</time>
<address>Contact information</address>`,
          note: "Helps search engines understand content structure",
        },
      ],
    },
    {
      title: "Meta & SEO",
      items: [
        {
          title: "Meta Tags",
          content: "Important meta tags for SEO",
          code: `<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="description" content="Page description">
<meta name="keywords" content="html, web, development">
<meta name="author" content="Author Name">
<meta name="robots" content="index, follow">

<!-- Open Graph -->
<meta property="og:title" content="Page Title">
<meta property="og:description" content="Description">
<meta property="og:image" content="image.jpg">
<meta property="og:url" content="https://example.com">

<!-- Twitter Card -->
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="Page Title">`,
          note: "Essential for SEO and social sharing",
        },
        {
          title: "Link Tags",
          content: "Resource and relationship links",
          code: `<link rel="stylesheet" href="styles.css">
<link rel="icon" href="favicon.ico">
<link rel="apple-touch-icon" href="icon.png">
<link rel="canonical" href="https://example.com/page">
<link rel="alternate" hreflang="es" href="https://example.com/es">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preload" href="font.woff2" as="font">`,
          note: "Helps with resource loading and SEO",
        },
      ],
    },
  ],
};

export default html5CheatSheet;
